import React, { useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Play, RefreshCw, Save } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CronScheduleEditor } from './CronScheduleEditor';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from '@/components/ui/toast';

type MailDeliveryMode = 'debug' | 'test' | 'full';

const MAIL_DELIVERY_MODE_OPTIONS: Array<{ label: string; value: MailDeliveryMode }> = [
  { label: 'None', value: 'debug' },
  { label: 'Test', value: 'test' },
  { label: 'Production', value: 'full' },
];

const isMailDeliveryMode = (value: unknown): value is MailDeliveryMode =>
  MAIL_DELIVERY_MODE_OPTIONS.some(option => option.value === value);

const ADMIN_CRON_JOBS = gql`
  query AdminCronJobs {
    adminCronJobs {
      name
      label
      timezone
      configured
      enabled
      schedule
      runPending
      runRequestedAt
      runRequestedBy
      mailDeliveryMode
    }
  }
`;

const UPDATE_ADMIN_CRON_JOB = gql`
  mutation UpdateAdminCronJob($input: AdminCronJobInput!) {
    updateAdminCronJob(input: $input) {
      name
      enabled
      schedule
    }
  }
`;

const TRIGGER_ADMIN_CRON_JOB = gql`
  mutation TriggerAdminCronJob($name: String!) {
    triggerAdminCronJob(name: $name) {
      name
      runPending
      runRequestedAt
      runRequestedBy
    }
  }
`;

type CronJob = {
  name: string;
  label: string;
  timezone: string;
  configured: boolean;
  enabled: boolean;
  schedule: string;
  runPending: boolean;
  runRequestedAt?: string | null;
  runRequestedBy?: string | null;
  mailDeliveryMode?: MailDeliveryMode | null;
};

export const CronManagementPage: React.FC = () => {
  const [draftSchedules, setDraftSchedules] = useState<Record<string, string>>({});
  const [draftMailDeliveryMode, setDraftMailDeliveryMode] = useState<MailDeliveryMode | ''>('');
  const [runTarget, setRunTarget] = useState<CronJob | null>(null);
  const { data, loading, refetch } = useQuery<{ adminCronJobs: CronJob[] }>(ADMIN_CRON_JOBS, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 5000,
  });
  const [updateCronJob, { loading: updating }] = useMutation(UPDATE_ADMIN_CRON_JOB);
  const [triggerCronJob, { loading: triggering }] = useMutation(TRIGGER_ADMIN_CRON_JOB);
  const jobs = useMemo(() => data?.adminCronJobs || [], [data]);
  const configuredMailDeliveryMode = jobs.find(job => job.name === 'processMailQueue')?.mailDeliveryMode;

  useEffect(() => {
    if (!configuredMailDeliveryMode) return;
    setDraftMailDeliveryMode(configuredMailDeliveryMode);
  }, [configuredMailDeliveryMode]);

  const scheduleFor = (job: CronJob) => draftSchedules[job.name] ?? (job.enabled ? job.schedule : '');

  const saveJob = async (job: CronJob) => {
    try {
      const schedule = scheduleFor(job);
      const mailDeliveryMode = job.name === 'processMailQueue'
        ? draftMailDeliveryMode
        : undefined;
      await updateCronJob({
        variables: {
          input: {
            name: job.name,
            enabled: Boolean(schedule.trim()),
            schedule,
            ...(mailDeliveryMode ? { mailDeliveryMode } : {}),
          },
        },
      });
      await refetch();
      setDraftSchedules(current => {
        const next = { ...current };
        delete next[job.name];
        return next;
      });
      toast.success(`${job.label} updated`);
    } catch (error: any) {
      toast.error(error?.message || `Unable to update ${job.label}`);
    }
  };

  const runNow = async () => {
    if (!runTarget) return;
    try {
      await triggerCronJob({ variables: { name: runTarget.name } });
      await refetch();
      toast.success(`${runTarget.label} queued to run`);
      setRunTarget(null);
    } catch (error: any) {
      toast.error(error?.message || `Unable to run ${runTarget.label}`);
    }
  };

  return (
    <div className="space-y-4 p-2">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Cron Management</CardTitle>
            <CardDescription>
              Choose a frequency or Disabled, then Save. Run now queues a one-time execution.
            </CardDescription>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => refetch()} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job</TableHead>
                  <TableHead>Recurring</TableHead>
                  <TableHead>Schedule</TableHead>
                  <TableHead>Job settings</TableHead>
                  <TableHead>Time zone</TableHead>
                  <TableHead>Manual run</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!loading && jobs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center text-sm text-gray-500">
                      No cron jobs are available.
                    </TableCell>
                  </TableRow>
                )}
                {jobs.map(job => (
                  <TableRow key={job.name}>
                    <TableCell>
                      <div className="font-medium">{job.label}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={job.enabled ? 'default' : 'secondary'}
                          className={job.enabled ? 'bg-[var(--semantic-success)] text-[var(--surface-panel-solid)] hover:bg-[var(--semantic-success)]' : undefined}
                        >
                          {job.enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="min-w-[220px]">
                      <CronScheduleEditor
                        id={`cron-${job.name}-schedule`}
                        label={job.label}
                        disabled={updating}
                        value={scheduleFor(job)}
                        onChange={schedule => setDraftSchedules(current => ({
                          ...current,
                          [job.name]: schedule,
                        }))}
                      />
                    </TableCell>
                    <TableCell className="min-w-[220px]">
                      {job.name === 'processMailQueue' ? (
                        <div className="flex items-end gap-2">
                          <div className="w-36 space-y-1">
                            <label htmlFor="cron-process-mail-queue-delivery-mode" className="text-xs font-medium">
                              Delivery mode
                            </label>
                            <Select
                              value={draftMailDeliveryMode || undefined}
                              disabled={updating}
                              onValueChange={value => {
                                if (!isMailDeliveryMode(value)) return;
                                setDraftMailDeliveryMode(value);
                              }}
                            >
                              <SelectTrigger
                                id="cron-process-mail-queue-delivery-mode"
                                aria-label="Mail Queue Processor delivery mode"
                              >
                                <SelectValue placeholder="Select mode" />
                              </SelectTrigger>
                              <SelectContent>
                                {MAIL_DELIVERY_MODE_OPTIONS.map(option => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">None</span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{job.timezone}</TableCell>
                    <TableCell>
                      {job.runPending ? (
                        <div>
                          <Badge variant="outline">Queued</Badge>
                          <div className="mt-1 text-xs text-gray-500">
                            {job.runRequestedBy || 'Unknown user'}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">None</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={updating}
                          id={`cron-${job.name}-save`}
                          onClick={() => saveJob(job)}
                        >
                          <Save className="mr-1.5 h-4 w-4" />
                          Save
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          disabled={job.runPending || triggering}
                          onClick={() => setRunTarget(job)}
                        >
                          <Play className="mr-1.5 h-4 w-4" />
                          Run now
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      <AlertDialog open={Boolean(runTarget)} onOpenChange={open => !open && setRunTarget(null)}>
        <AlertDialogContent className="bg-[var(--surface-panel-solid)]">
          <AlertDialogHeader>
            <AlertDialogTitle>Run {runTarget?.label} now?</AlertDialogTitle>
            <AlertDialogDescription>
              This queues one execution on the cron service. It does not change the recurring enabled state or schedule.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:space-x-0">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runNow} disabled={triggering}>
              {triggering ? 'Queueing...' : 'Run now'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CronManagementPage;
